/**
 * 🏷 국세청 자동분류 거래처 명단 — 수출입 화면 아래 절 (2026-09-08 · v2.131.0).
 *
 * 무엇을 하는 화면인가
 *   국세청 매입계산서를 올렸을 때 그 줄을 「수입 물류비」·「세관 수입 부가세」로 보낼
 *   거래처 명단이다. 여기 없는 곳은 전부 포털 매입(판관비)으로 등록된다.
 *   지금까지 이 명단은 코드에 박혀 있어 한 곳 추가에 배포가 필요했다.
 *
 * ⚠ 명단을 고쳐도 **이미 처리된 계산서는 다시 분류되지 않는다** — 업로드가 승인번호를
 *   보고 판정 전에 건너뛰기 때문이다. 화면에 그렇게 적어 둔다(안 적으면 문의가 온다).
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../lib/import-partner-api';
import type { ImportPartnerRow, ImportRouteKind, PartnerCandidate, PartnerInput } from '../lib/import-partner-api';
import { LoadingLines } from './ui/Skeleton';

const fmtKrw = (n: number | null | undefined): string =>
  n == null ? '-' : Math.round(n).toLocaleString('ko-KR');

const fmtBno = (b: string): string =>
  b && b.length === 10 ? `${b.slice(0, 3)}-${b.slice(3, 5)}-${b.slice(5)}` : b || '-';

const ROUTE_BADGE: Record<ImportRouteKind, string> = {
  물류: 'bg-sky-100 text-sky-700',
  부가세: 'bg-violet-100 text-violet-700',
};
const ROUTE_LABEL: Record<ImportRouteKind, string> = {
  물류: '🚢 수입 물류비',
  부가세: '🛃 세관 부가세',
};

const emptyInput = (): PartnerInput => ({
  route: '물류', displayName: '', bno: '', matchByName: false, nameToken: '', note: '', isActive: true,
});

export function ImportPartnerSection() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showCandidates, setShowCandidates] = useState(false);
  const [form, setForm] = useState<PartnerInput>(emptyInput);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [candSearch, setCandSearch] = useState('');

  const list = useQuery({
    queryKey: ['importPartners'],
    queryFn: api.listPartners,
    enabled: open,
    staleTime: 30_000,
  });
  const cands = useQuery({
    queryKey: ['importPartnerCandidates'],
    queryFn: api.listCandidates,
    enabled: open && showCandidates,
    staleTime: 30_000,
  });

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['importPartners'] });
    void qc.invalidateQueries({ queryKey: ['importPartnerCandidates'] });
  };

  const create = useMutation({
    mutationFn: (input: PartnerInput) =>
      api.createPartner(input, (msg) => window.confirm(msg)),
    onSuccess: () => { setForm(emptyInput()); setShowAdd(false); refresh(); },
    onError: (e: Error) => window.alert(e.message),
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: number; input: PartnerInput }) => api.updatePartner(id, input),
    onSuccess: () => { setEditingId(null); setForm(emptyInput()); refresh(); },
    onError: (e: Error) => window.alert(e.message),
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.deletePartner(id),
    onSuccess: refresh,
    onError: (e: Error) => window.alert(e.message),
  });

  const rows = list.data?.rows ?? [];
  const totals = useMemo(() => {
    const c = rows.reduce((s, r) => s + r.stats.count, 0);
    const a = rows.reduce((s, r) => s + r.stats.amount, 0);
    return { count: c, amount: a };
  }, [rows]);

  const candRows: PartnerCandidate[] = useMemo(() => {
    const all = cands.data ?? [];
    const q = candSearch.trim();
    if (!q) return all;
    return all.filter((c) => c.supplier.includes(q) || c.bno.includes(q.replace(/\D/g, '')));
  }, [cands.data, candSearch]);

  const startEdit = (r: ImportPartnerRow): void => {
    setEditingId(r.id);
    setShowAdd(true);
    setForm({
      // 표에는 숫자만 저장돼 있다 — 고칠 때는 사람이 읽는 모양(000-00-00000)으로 보여 준다.
      route: r.route, displayName: r.displayName, bno: fmtBno(r.bno) === '-' ? '' : fmtBno(r.bno),
      matchByName: !!r.nameToken, nameToken: r.nameToken, note: r.note, isActive: r.isActive,
    });
  };

  const addFromCandidate = (c: PartnerCandidate, route: ImportRouteKind): void => {
    setEditingId(null);
    setShowAdd(true);
    setShowCandidates(false);
    setForm({ ...emptyInput(), route, displayName: c.supplier, bno: c.bno, note: '' });
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const submit = (): void => {
    if (!form.displayName.trim()) { window.alert('상호를 적어 주세요.'); return; }
    if (editingId) update.mutate({ id: editingId, input: form });
    else create.mutate(form);
  };

  return (
    <div className="mt-6 bg-white rounded border border-slate-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-slate-50 rounded"
      >
        <span className="text-base font-bold text-slate-800">🏷 국세청 자동분류 거래처</span>
        <span className="text-xs text-slate-500">
          매입계산서를 올렸을 때 수출입 비용으로 보낼 곳 명단
        </span>
        <span className="ml-auto text-slate-400 text-sm">{open ? '접기 ▲' : '펼치기 ▼'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-slate-100">
          {list.isLoading && <div className="py-8 text-center text-slate-400 text-sm"><LoadingLines /></div>}
          {list.isError && (
            <div className="py-6 text-center text-sm text-rose-600">
              명단을 불러오지 못했습니다 — {(list.error as Error).message}
            </div>
          )}

          {list.data && (
            <>
              {list.data.fallback && (
                <div className="mt-3 px-3 py-2 rounded bg-rose-50 border border-rose-200 text-xs text-rose-700">
                  ⚠ 명단 표를 읽지 못해 <b>기본 명단</b>으로 돌고 있습니다. 여기서 고친 내용이 반영되지 않습니다 — 관리자에게 알려 주세요.
                </div>
              )}

              <div className="mt-3 mb-2 text-xs text-slate-600 leading-relaxed bg-slate-50 rounded px-3 py-2">
                여기 있는 거래처의 매입계산서는 <b>매입(판관비)이 아니라 수출입 비용</b>으로 등록됩니다
                (물류 = 오더에 배분해 원가에 반영 · 부가세 = 공제 대상이라 원가에 넣지 않음).
                <br />
                · 상호가 <b>「세관」으로 끝나는 곳</b>은 명단에 없어도 자동으로 부가세로 갑니다
                {list.data.builtin.stats.count > 0 && <>({list.data.builtin.stats.count}건 적용됨)</>}.
                <br />
                · <b>명단을 고쳐도 이미 올린 계산서는 다시 분류되지 않습니다</b> — 지난 건은 매입 화면의
                {' '}[🚢 수입 물류비인가요?] 로 옮기세요.
              </div>

              <div className="flex items-center gap-2 mb-2">
                <div className="text-xs text-slate-500">
                  {rows.length}곳 · 지금까지 자동분류 <b className="text-slate-700">{totals.count}건</b>
                  {' '}({fmtKrw(totals.amount)}원)
                  {list.data.orphan.count > 0 && (
                    <span className="ml-2 text-amber-700">
                      · 명단에서 빠진 뒤 남은 과거 건 {list.data.orphan.count}건
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { setShowCandidates((v) => !v); setShowAdd(false); }}
                  className="btn btn-secondary btn-sm ml-auto"
                >
                  🔎 명단에 없는 공급자 찾기
                </button>
                <button
                  type="button"
                  onClick={() => { setEditingId(null); setForm(emptyInput()); setShowAdd((v) => !v); setShowCandidates(false); }}
                  className="btn btn-primary btn-sm"
                >
                  + 거래처 추가
                </button>
              </div>

              {/* ── 추가·수정 폼 ── */}
              {showAdd && (
                <div className="mb-3 p-3 rounded border border-blue-200 bg-blue-50/50">
                  <div className="flex flex-wrap items-end gap-2">
                    <label className="ui-label block">
                      <span className="block text-slate-600 font-semibold mb-1">구분</span>
                      <select
                        value={form.route}
                        onChange={(e) => setForm({ ...form, route: e.target.value as ImportRouteKind })}
                        className="ui-input"
                      >
                        <option value="물류">🚢 수입 물류비</option>
                        <option value="부가세">🛃 세관 부가세</option>
                      </select>
                    </label>
                    <label className="ui-label block flex-1 min-w-[200px]">
                      <span className="block text-slate-600 font-semibold mb-1">상호</span>
                      <input
                        value={form.displayName}
                        onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                        placeholder="계산서에 찍히는 회사 이름"
                        className="ui-input w-full"
                      />
                    </label>
                    <label className="ui-label block">
                      <span className="block text-slate-600 font-semibold mb-1">사업자번호</span>
                      <input
                        value={form.bno}
                        onChange={(e) => setForm({ ...form, bno: e.target.value })}
                        placeholder="104-81-17971"
                        className="ui-input w-40 tabular-nums"
                      />
                    </label>
                    <label className="ui-label block flex-1 min-w-[180px]">
                      <span className="block text-slate-600 font-semibold mb-1">메모 (근거)</span>
                      <input
                        value={form.note}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                        placeholder="예) 항공운임·하역비"
                        className="ui-input w-full"
                      />
                    </label>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs text-slate-700">
                      <input
                        type="checkbox"
                        checked={form.matchByName}
                        onChange={(e) => setForm({ ...form, matchByName: e.target.checked, nameToken: e.target.checked ? (form.nameToken || form.displayName) : '' })}
                      />
                      상호로도 잡기
                      <span className="text-slate-400">(계산서에 사업자번호가 비어 오는 경우 대비)</span>
                    </label>
                    {form.matchByName && (
                      <input
                        value={form.nameToken}
                        onChange={(e) => setForm({ ...form, nameToken: e.target.value })}
                        placeholder="잡을 글자 — 지점명·(주) 는 빼고 회사 이름만"
                        className="ui-input flex-1 min-w-[220px]"
                      />
                    )}
                    <label className="flex items-center gap-1.5 text-xs text-slate-700">
                      <input
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                      />
                      사용
                    </label>
                    <div className="ml-auto flex gap-2">
                      <button
                        type="button"
                        onClick={() => { setShowAdd(false); setEditingId(null); setForm(emptyInput()); }}
                        className="btn btn-secondary"
                      >취소</button>
                      <button
                        type="button"
                        onClick={submit}
                        disabled={create.isPending || update.isPending}
                        className="btn btn-primary"
                      >{editingId ? '수정 저장' : '명단에 추가'}</button>
                    </div>
                  </div>
                  {form.matchByName && (
                    <p className="mt-1.5 text-xs text-amber-700">
                      ⚠ 「항공」·「물류」·「운송」 같은 일반어는 넣지 마세요 — 부분일치라 엉뚱한 계산서까지 딸려옵니다
                      (실제로 롯데렌탈 차종 이름의 「에어」, 퀵서비스 「운송료」가 걸린 적이 있습니다).
                    </p>
                  )}
                </div>
              )}

              {/* ── 명단에 없는 공급자 후보 ── */}
              {showCandidates && (
                <div className="mb-3 p-3 rounded border border-slate-200 bg-slate-50">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="text-xs text-slate-600">
                      국세청 업로드로 <b>매입(판관비)</b>에 등록된 공급자 중 명단에 없는 곳입니다.
                      물류·통관 회사가 보이면 명단에 넣으세요.
                    </div>
                    <input
                      value={candSearch}
                      onChange={(e) => setCandSearch(e.target.value)}
                      placeholder="상호·사업자번호 검색"
                      className="ui-input ui-input-sm ml-auto w-52"
                    />
                  </div>
                  {cands.isLoading && <div className="py-4 text-center text-slate-500 text-xs"><LoadingLines /></div>}
                  {cands.data && (
                    <div className="max-h-72 overflow-y-auto overflow-x-auto">
                      <table className="data-table w-full text-xs bg-white">
                        <thead>
                          <tr>
                            <th className="text-left">공급자</th>
                            <th className="text-left w-36">사업자번호</th>
                            <th className="text-right w-16">건수</th>
                            <th className="text-right w-28">금액</th>
                            <th className="text-left w-24">최근</th>
                            <th className="text-center w-44">명단에 넣기</th>
                          </tr>
                        </thead>
                        <tbody>
                          {candRows.length === 0 && (
                            <tr><td colSpan={6} className="py-4 text-center text-slate-400">해당하는 공급자가 없습니다.</td></tr>
                          )}
                          {candRows.map((c) => (
                            <tr key={`${c.bno}|${c.supplier}`}>
                              <td className="text-left">{c.supplier || '(상호 없음)'}</td>
                              <td className="text-left tabular-nums">{fmtBno(c.bno)}</td>
                              <td className="text-right tabular-nums">{c.count}</td>
                              <td className="text-right tabular-nums">{fmtKrw(c.amount)}</td>
                              <td className="text-left">{c.lastDate ?? '-'}</td>
                              <td className="text-center whitespace-nowrap">
                                <button
                                  type="button"
                                  onClick={() => addFromCandidate(c, '물류')}
                                  className="btn btn-outline btn-sm mr-1"
                                >🚢 물류</button>
                                <button
                                  type="button"
                                  onClick={() => addFromCandidate(c, '부가세')}
                                  className="btn btn-outline btn-sm"
                                >🛃 세관</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ── 명단 ── */}
              <div className="overflow-x-auto">
                <table className="data-table w-full text-[13px]">
                  <thead>
                    <tr>
                      <th className="text-left w-32">구분</th>
                      <th className="text-left min-w-[180px]">거래처</th>
                      <th className="text-left w-36">사업자번호</th>
                      <th className="text-right w-16">건수</th>
                      <th className="text-right w-28">금액</th>
                      <th className="text-left w-24">최근</th>
                      <th className="text-left min-w-[140px]">메모</th>
                      <th className="text-center w-16">사용</th>
                      <th className="text-center w-20">관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr><td colSpan={9} className="py-6 text-center text-slate-400">명단이 비어 있습니다.</td></tr>
                    )}
                    {rows.map((r) => (
                      <tr key={r.id} className={r.isActive ? '' : 'opacity-50'}>
                        <td className="text-left whitespace-nowrap">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${ROUTE_BADGE[r.route]}`}>
                            {ROUTE_LABEL[r.route]}
                          </span>
                        </td>
                        <td className="text-left">
                          <div className="font-medium text-slate-800">{r.displayName}</div>
                          {r.nameToken && (
                            <div className="text-xs text-slate-500">상호로도 잡음: {r.nameToken}</div>
                          )}
                        </td>
                        <td className="text-left tabular-nums">{fmtBno(r.bno)}</td>
                        <td className="text-right tabular-nums">{r.stats.count || '-'}</td>
                        <td className="text-right tabular-nums">{r.stats.count ? fmtKrw(r.stats.amount) : '-'}</td>
                        <td className="text-left">{r.stats.lastDate ?? '-'}</td>
                        <td className="text-left text-xs text-slate-600">{r.note || '-'}</td>
                        <td className="text-center">{r.isActive ? '✅' : '⛔'}</td>
                        <td className="text-center whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => startEdit(r)}
                            className="px-1 text-slate-500 hover:text-blue-600"
                            title="수정"
                          >✎</button>
                          <button
                            type="button"
                            onClick={() => {
                              const warn = r.stats.count > 0
                                ? `\n\n지금까지 이 거래처로 ${r.stats.count}건(${fmtKrw(r.stats.amount)}원)이 수출입 비용으로 등록됐습니다.\n이미 등록된 비용은 삭제되지 않고, 앞으로 올라오는 계산서만 매입(판관비)으로 갑니다.`
                                : '';
                              if (window.confirm(`「${r.displayName}」을 자동분류 명단에서 뺄까요?${warn}`)) remove.mutate(r.id);
                            }}
                            className="px-1 text-slate-500 hover:text-rose-600"
                            title="명단에서 빼기"
                          >🗑</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
